<template>
  <el-drawer
    v-model="visible"
    :size="drawerWidth"
    :with-header="false"
    destroy-on-close
    class="post-drawer"
  >
    <div v-if="postsStore.selectedPost" class="drawer-content">
      <!-- Заголовок -->
      <div class="drawer-header">
        <div class="drawer-title">
          <span>Пост #{{ postsStore.selectedPost.id }}</span>
          <el-tag :type="statusType(postsStore.selectedPost.status)" size="small" effect="light">
            {{ statusLabel(postsStore.selectedPost.status) }}
          </el-tag>
          <el-tag v-if="postsStore.selectedPost.category" size="small" :type="categoryType(postsStore.selectedPost.category)" effect="light">
            {{ postsStore.selectedPost.category }}
          </el-tag>
        </div>
        <div class="drawer-meta">
          <span>📅 {{ formatDate(postsStore.selectedPost.published_at || postsStore.selectedPost.scraped_at) }}</span>
          <span>📡 {{ postsStore.selectedPost.source }} / {{ postsStore.selectedPost.source_id }}</span>
          <span v-if="postsStore.selectedPost.federal_district">
            🗺️ {{ postsStore.selectedPost.federal_district }}
            <span v-if="postsStore.selectedPost.region_name">({{ postsStore.selectedPost.region_name }})</span>
          </span>
          <span v-if="postsStore.selectedPost.views">👁 {{ postsStore.selectedPost.views }} просмотров</span>
          <span v-if="postsStore.selectedPost.replies">💬 {{ postsStore.selectedPost.replies }} ответов</span>
          <span v-if="postsStore.selectedPost.forwards">↗️ {{ postsStore.selectedPost.forwards }} репостов</span>
        </div>
      </div>

      <el-divider />

      <!-- Содержание -->
      <div class="drawer-section">
        <h4>📝 Содержание</h4>
        <div class="content-box">
          <MarkdownRenderer :text="postsStore.selectedPost.content" />
        </div>
      </div>

      <!-- OCR-текст -->
      <div v-if="postsStore.selectedPost.image_text" class="drawer-section">
        <h4>🔍 Текст с изображения (OCR)</h4>
        <div class="ocr-box">
          <MarkdownRenderer :text="postsStore.selectedPost.image_text" />
        </div>
      </div>

      <!-- Обоснование LLM -->
      <div v-if="postsStore.selectedPost.explanation" class="drawer-section">
        <h4>🤖 Обоснование ИИ</h4>
        <div class="llm-box">
          <MarkdownRenderer :text="postsStore.selectedPost.explanation" />
          <div v-if="postsStore.selectedPost.confidence" class="confidence">
            Уверенность: {{ (postsStore.selectedPost.confidence * 100).toFixed(1) }}%
          </div>
          <div v-if="postsStore.selectedPost.llm_model" class="model">
            Модель: {{ postsStore.selectedPost.llm_model }}
          </div>
        </div>
      </div>

      <!-- История классификации -->
      <div v-if="postsStore.selectedPost.classifications?.length" class="drawer-section">
        <h4>📊 История классификации</h4>
        <el-timeline>
          <el-timeline-item
            v-for="(c, idx) in postsStore.selectedPost.classifications"
            :key="idx"
            :type="c.label === 'YES' ? 'success' : c.label === 'UNCERTAIN' ? 'warning' : 'primary'"
          >
            <div class="classification-item">
              <strong>{{ c.stage }}</strong> → {{ c.label }}
              <span v-if="c.confidence">({{ (c.confidence * 100).toFixed(0) }}%)</span>
              <div class="classification-reason">
                <MarkdownRenderer :text="c.reason" />
              </div>
              <div class="classification-model">{{ c.model }} • {{ formatDate(c.created_at) }}</div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>

      <!-- Внешняя ссылка -->
      <div v-if="postsStore.selectedPost.url" class="drawer-section">
        <el-link :href="postsStore.selectedPost.url" target="_blank" type="primary">
          🔗 Открыть оригинал
        </el-link>
      </div>

      <el-divider />

      <!-- Действия валидации -->
      <div v-if="authStore.canValidatePosts" class="drawer-actions">
        <div class="actions-label">Решение валидатора:</div>
        <el-button-group class="action-group">
          <el-button
            type="success"
            size="large"
            @click="validate('COMPLAINT')"
            :loading="validating"
            aria-keyshortcuts="1"
          >
            <el-icon><Check /></el-icon>
            Жалоба <kbd class="hotkey">1</kbd>
          </el-button>
          <el-button
            type="danger"
            size="large"
            @click="validate('FALSE_POS')"
            :loading="validating"
            aria-keyshortcuts="2"
          >
            <el-icon><Close /></el-icon>
            Ложное сраб. <kbd class="hotkey">2</kbd>
          </el-button>
        </el-button-group>
      </div>
    </div>

    <!-- Состояние загрузки -->
    <el-skeleton v-else :rows="10" animated />
  </el-drawer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Check, Close } from '@element-plus/icons-vue'
import { usePostsStore } from '@/stores/posts'
import { useAuthStore } from '@/stores/auth'
import MarkdownRenderer from '@/components/MarkdownRenderer.vue'
import { formatAppDateTime } from '@/config/timezone'
import { validationVerdictFromHotkey, type ValidationVerdict } from '@/components/post-hotkeys'

const props = defineProps<{
  modelValue: boolean
  postId: number | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'validated', result: { viaHotkey: boolean; nextPostId: number | null }): void
}>()

const postsStore = usePostsStore()
const authStore = useAuthStore()
const validating = ref(false)

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const drawerWidth = computed(() => {
  return window.innerWidth < 768 ? '100%' : '560px'
})

watch(() => props.postId, async (id) => {
  if (id !== null) {
    await postsStore.loadPostDetail(id)
  }
}, { immediate: true })

onMounted(() => window.addEventListener('keydown', handleValidationHotkey))
onUnmounted(() => window.removeEventListener('keydown', handleValidationHotkey))

async function validate(verdict: ValidationVerdict, viaHotkey = false) {
  if (!props.postId || validating.value) return

  const currentPostId = props.postId
  const nextPostId = viaHotkey ? findNextPostId(currentPostId) : null
  validating.value = true
  try {
    const ok = await postsStore.validatePost(currentPostId, verdict)
    if (ok) {
      emit('validated', { viaHotkey, nextPostId })
    }
  } finally {
    validating.value = false
  }
}

function findNextPostId(currentPostId: number): number | null {
  const currentIndex = postsStore.filteredPosts.findIndex((post) => post.id === currentPostId)
  const followingPost = currentIndex >= 0 ? postsStore.filteredPosts[currentIndex + 1] : null

  return followingPost?.id
    ?? postsStore.filteredPosts.find((post) => post.id !== currentPostId)?.id
    ?? null
}

function handleValidationHotkey(event: KeyboardEvent) {
  if (!visible.value || !authStore.canValidatePosts || !props.postId || validating.value) return

  const verdict = validationVerdictFromHotkey(event)
  if (!verdict) return

  event.preventDefault()
  void validate(verdict, true)
}

function formatDate(dateStr: string | null): string {
  return formatAppDateTime(dateStr, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusType(status: string): '' | 'success' | 'warning' | 'info' {
  switch (status?.toLowerCase()) {
    case 'flagged': return 'warning'
    case 'validated': return 'success'
    case 'archived': return 'info'
    default: return 'info'
  }
}

function statusLabel(status: string): string {
  switch (status?.toLowerCase()) {
    case 'new': return 'Новый'
    case 'flagged': return 'Отмечен'
    case 'validated': return 'Подтверждён'
    case 'in_report': return 'В отчёте'
    case 'archived': return 'Архив'
    default: return status
  }
}

function categoryType(category: string): '' | 'success' | 'warning' | 'danger' | 'primary' | 'info' {
  switch (category?.toLowerCase()) {
    case 'medical': return 'primary'
    case 'payment': return 'success'
    case 'abuse': return 'danger'
    case 'conditions': return 'warning'
    case 'equipment': return 'info'
    case 'command': return 'info'
    case 'mobilization': return 'primary'
    case 'rights': return 'success'
    case 'mental_health': return 'danger'
    default: return 'info'
  }
}
</script>

<style scoped>
.drawer-content {
  padding: 8px;
}

.drawer-header {
  margin-bottom: 12px;
}

.drawer-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 700;
  color: var(--app-text-primary);
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.drawer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 14px;
  color: var(--app-text-regular);
  font-weight: 600;
}

.drawer-section {
  margin-bottom: 16px;
}

.drawer-section h4 {
  font-size: 16px;
  font-weight: 700;
  color: var(--app-text-primary);
  margin-bottom: 8px;
}

.content-box {
  background: var(--app-bg-muted);
  padding: 14px;
  border-radius: 6px;
}

.ocr-box {
  background: var(--app-success-soft);
  padding: 14px;
  border-radius: 6px;
}

.ocr-box :deep(.markdown-body) {
  color: var(--app-text-regular);
  font-style: italic;
}

.llm-box {
  background: var(--app-info-soft);
  padding: 14px;
  border-radius: 6px;
}

.confidence {
  margin-top: 10px;
  font-size: 14px;
  color: #409eff;
  font-weight: 700;
}

.model {
  margin-top: 6px;
  font-size: 13px;
  color: var(--app-text-secondary);
  font-weight: 600;
}

.classification-item {
  font-size: 15px;
}

.classification-reason {
  margin-top: 6px;
  color: var(--app-text-regular);
  font-size: 14px;
  background: var(--app-bg-muted);
  padding: 8px;
  border-radius: 4px;
}

.classification-model {
  margin-top: 6px;
  color: var(--app-text-secondary);
  font-size: 13px;
  font-weight: 600;
}

.drawer-actions {
  padding: 12px 0;
}

.actions-label {
  font-size: 16px;
  font-weight: 700;
  color: var(--app-text-primary);
  margin-bottom: 12px;
}

.action-group {
  width: 100%;
}

.action-group :deep(.el-button) {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
}

.hotkey {
  margin-left: 5px;
  padding: 1px 6px;
  border: 1px solid currentColor;
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
  line-height: 18px;
  opacity: 0.8;
}
</style>
